import { assign, createMachine } from "xstate";

export interface StepDefinition {
  id: string;
  /** Step is not a valid BACK landing target; BACK skips over it. Used for auto-advancing interstitials that would immediately re-advance. */
  skipOnBack?: boolean;
}

export type PostalLocationContext = {
  postalCode: string;
  location: string;
};

/** Minimal initial context for the lead form machine. Caller (e.g. LeadForm) builds this from steps and optional office postal fields. */
export type LeadFormMachineInitialContext = {
  persistedFieldValues?: Record<string, string>;
  postalLocationValues?: PostalLocationContext;
};

const defaultContext = {
  errors: {} as Record<string, string[]>,
  persistedFieldValues: {} as Record<string, string>,
  postalLocationValues: {
    location: "",
    postalCode: "",
  } as PostalLocationContext,
};

type MachineContext = typeof defaultContext;

function getInitialContext(
  input?: LeadFormMachineInitialContext,
): MachineContext {
  return {
    errors: {},
    persistedFieldValues: input?.persistedFieldValues ?? {},
    postalLocationValues:
      input?.postalLocationValues ?? defaultContext.postalLocationValues,
  };
}

type MachineEvents =
  | { type: "NEXT" }
  | { type: "BACK" }
  | { type: "VALIDATION_ERROR"; errors: Record<string, string[]> }
  | { type: "FIELD_CHANGE"; name: string; value: string }
  | { type: "POSTAL_LOCATION_CHANGE"; postalCode: string; location: string };

type FieldChangeEvent = Extract<MachineEvents, { type: "FIELD_CHANGE" }>;
type PostalLocationChangeEvent = Extract<
  MachineEvents,
  { type: "POSTAL_LOCATION_CHANGE" }
>;

/**
 * Creates an XState machine with named states from an array of step definitions.
 * Each step becomes a state node with linear NEXT/BACK transitions.
 * VALIDATION_ERROR stores field errors in context without advancing the step.
 * FIELD_CHANGE and POSTAL_LOCATION_CHANGE update persisted form values in context.
 * NEXT and BACK transitions clear stored errors only.
 * Optional initialContext is merged into default context (for postal prefill, etc.).
 */
export function createLeadFormMachine(
  steps: StepDefinition[],
  initialContext?: LeadFormMachineInitialContext,
) {
  if (steps.length === 0) {
    throw new Error("Cannot create a step machine with zero steps.");
  }

  const states: Record<string, { on?: Record<string, unknown> }> = {};

  for (let i = 0; i < steps.length; i++) {
    const stepId = steps[i].id;
    const on: Record<string, unknown> = {};

    if (i < steps.length - 1) {
      on.NEXT = {
        actions: assign({ errors: {} as Record<string, string[]> }),
        target: steps[i + 1].id,
      };
    }

    if (i > 0) {
      let backIndex = i - 1;
      while (backIndex >= 0 && steps[backIndex].skipOnBack) {
        backIndex--;
      }
      if (backIndex >= 0) {
        on.BACK = {
          actions: assign({ errors: {} as Record<string, string[]> }),
          target: steps[backIndex].id,
        };
      }
    }

    on.VALIDATION_ERROR = {
      actions: assign({
        errors: ({ event }: { event: { errors: Record<string, string[]> } }) =>
          event.errors,
      }),
    };

    on.FIELD_CHANGE = {
      actions: assign({
        persistedFieldValues: ({
          context,
          event,
        }: {
          context: MachineContext;
          event: FieldChangeEvent;
        }) => ({
          ...context.persistedFieldValues,
          [event.name]: event.value,
        }),
      }),
    };

    on.POSTAL_LOCATION_CHANGE = {
      actions: assign({
        postalLocationValues: ({
          event,
        }: {
          context: MachineContext;
          event: PostalLocationChangeEvent;
        }) => ({
          location: event.location,
          postalCode: event.postalCode,
        }),
      }),
    };

    states[stepId] = { on };
  }

  return createMachine({
    context: getInitialContext(initialContext),
    id: "leadForm",
    initial: steps[0].id,
    states,
    types: {} as {
      context: MachineContext;
      events: MachineEvents;
    },
  });
}
